import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import { playgroundLink } from '../../lib/ui-links.js';

interface EvaluationRunOptions {
  evaluation: string;
  input: string;
  execModel: string;
  evalModel?: string;
}

/**
 * Register the `evaluation run` subcommand on the `evaluation` Command node.
 * Extracted to keep evaluations.ts under the 500-line limit.
 */
export function registerEvaluationRunCommand(evaluation: Command, prompts: Command): void {
  evaluation
    .command('run')
    .description('Run a prompt against an evaluation (playground eval)')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .requiredOption('-e, --evaluation <id>', 'Evaluation ID to score against')
    .requiredOption('--input <json>', 'Input JSON object for the prompt (e.g. \'{"text":"hello"}\')')
    .requiredOption('--exec-model <model>', 'Model to use for generating the response (e.g. "claude-sonnet-4-5-20250929")')
    .option('--eval-model <model>', 'Model to use for evaluation scoring (defaults to exec-model)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation run <prompt-id> --evaluation <eval-id> --input '{"text":"hello"}' --exec-model claude-sonnet-4-5-20250929
  ${chalk.dim('$')} mutagent prompts evaluation run <prompt-id> -e <eval-id> --input '{"text":"hello"}' --exec-model claude-sonnet-4-5-20250929 --eval-model gpt-4o
  ${chalk.dim('$')} mutagent prompts evaluation run <prompt-id> -e <eval-id> --input '{"text":"hello"}' --exec-model claude-sonnet-4-5-20250929 --json

${chalk.yellow('AI Agent note:')} Always pass --json for structured output.
${chalk.dim('Response includes evaluation score, pass/fail, token usage, and dashboard links.')}
    `)
    .action(async (promptId: string, options: EvaluationRunOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Validate input JSON
        let parsedInput: Record<string, unknown>;
        try {
          const raw: unknown = JSON.parse(options.input);
          if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
            throw new MutagentError(
              'INVALID_JSON',
              `--input must be a JSON object, got ${Array.isArray(raw) ? 'array' : typeof raw}`,
              'Run: mutagent prompts evaluation run --help\n' +
              'Provide a JSON object: --input \'{"field": "value"}\''
            );
          }
          parsedInput = raw as Record<string, unknown>;
        } catch (e) {
          if (e instanceof MutagentError) throw e;
          throw new MutagentError(
            'INVALID_JSON',
            'Invalid JSON in --input flag',
            'Run: mutagent prompts evaluation run --help\n' +
            'Provide valid JSON: --input \'{"text": "hello"}\''
          );
        }

        const evaluationId = parseInt(options.evaluation, 10);
        if (isNaN(evaluationId)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Invalid evaluation ID: "${options.evaluation}" (must be numeric)`,
            'Run: mutagent prompts evaluation list <prompt-id> to find valid IDs'
          );
        }

        const llmConfig: Record<string, unknown> = {
          model: options.execModel,
          ...(options.evalModel ? { evalModel: options.evalModel } : {}),
        };

        const client = await getSDKClient();
        const result = await client.playgroundEval(promptId, {
          input: parsedInput,
          evaluationId,
          llmConfig,
        });

        if (isJson) {
          output.output({
            ...result,
            _links: {
              dashboard: playgroundLink(promptId),
              api: `/api/prompt/${promptId}/playground/eval`,
            },
          });
        } else {
          const evalResult = result.evaluation;
          const status = evalResult.passed ? chalk.green('PASSED') : chalk.red('FAILED');
          output.success(`Evaluation complete: ${status}`);
          output.info(`Score: ${evalResult.score.toFixed(3)}`);
          if (evalResult.reasoning) {
            output.info(`Reasoning: ${evalResult.reasoning}`);
          }
          if (typeof result.response === 'string') {
            output.info(`Response: ${result.response.substring(0, 200)}${result.response.length > 200 ? '...' : ''}`);
          }
          output.info(`Latency: ${String(result.metadata.latencyMs)}ms`);
          if (result.metadata.tokens) {
            const t = result.metadata.tokens;
            output.info(`Tokens: prompt=${String(t.prompt ?? 0)}, completion=${String(t.completion ?? 0)}`);
          }
          if (evalResult.criteria && evalResult.criteria.length > 0) {
            output.info('Criteria scores:');
            for (const c of evalResult.criteria) {
              const mark = c.passed ? chalk.green('✓') : chalk.red('✗');
              output.info(`  ${mark} ${c.name}: ${c.score.toFixed(3)}`);
            }
          }
          output.info(`Dashboard: ${playgroundLink(promptId)}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
